import React, { Component } from 'react';
import FadeIn from 'react-fade-in';
import _ from 'underscore';
import ShopInfoAccordion from './ShopInfoAccordion';

import AddressForm from '../../components/AddressForm';
import FieldWithHistory from '../../components/FieldWithHistory';
import FloatingButton from '../../components/FloatingButton';
import { Header } from '../../components/Header';
import LoaderButton from '../../components/LoaderButton';

import {
  customerIdValidate,
  generateFilterRegex,
  usernameValidate,
  uuidValidate,
  checkFilter,
  makeShopSummaryObject,
} from '../../libs/utils';

import {
  getUserSummary,
  getShopSummary,
  getShops,
  getShopSummaryByProperty,
  getSystemConfig,
  getShopOrders,
  getShopCustomers,
  getShopEvents,
  getShopVehicles,
  getShopParts,
  getShopServices,
} from '../../libs/db-lib';

import {
  formatPhoneNumber,
  getRecentSearches,
  setRecentSearches,
  storeClipboard,
  getLocalStorageShopNames,
} from '../../libs/utils-ts';

import {
  AddressQuery,
  ChildProps,
  RuntimeConfig,
  Shop,
  ShopName,
} from '../../types';

import HelpTooltip from '../../components/Tooltips/HelpTooltip';

interface GetShopSummaryState {
  queryType: string;
  query: string;
  queryError: string;
  helpText: string;
  address: AddressQuery;
  isLoading: boolean;
  queriedShops: Shop[];
  queriedShopsFiltered: Shop[];
  shopNames: ShopName[];
  filter: string;
  currentShopId: string;
  showShopInfo: boolean;
  config: RuntimeConfig;
  windowWidth: number;
  loadingShopNames: boolean;
}

let state: GetShopSummaryState = {
  queryType: 'SHOP ID',
  query: '',
  queryError: '',
  helpText: '',
  address: { country: 'US', state: 'AL' } as AddressQuery,
  isLoading: false,
  currentShopId: '',
  queriedShops: [] as Shop[],
  queriedShopsFiltered: [] as Shop[],
  shopNames: [] as ShopName[],
  filter: '',
  config: {
    baseUrl: '',
    braintreeMode: '',
    merchantId: '',
    plusBaseTechCerts: 0,
    standardBaseTechCerts: 0,
    prices: {},
    plusBaseUsers: 0,
    standardBaseUsers: 0,
  },
  windowWidth: 0,
  loadingShopNames: false,
  showShopInfo: false,
};

const topRef = React.createRef<HTMLDivElement>();

export default class GetShopSummary extends Component<
  ChildProps,
  GetShopSummaryState
> {
  constructor(props: ChildProps) {
    super(props);
    const searchParams = new URLSearchParams(window.location.search);

    const query = searchParams.get('query');
    const queryType = searchParams.get('queryType');
    if (query && queryType) {
      state.query = query;
      state.queryType = queryType;
    }
    // This will initial state on first time, but stores previous state
    // when navigating back to the page
    this.state = state;
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.props.location !== prevProps.location) {
      // URL has changed
      this.checkURLChange();
    }
  }

  checkURLChange() {
    const searchParams = new URLSearchParams(window.location.search);
    const query = searchParams.get('query')?.replace(/ /g, '+');
    const queryType = searchParams.get('queryType');
    const shopID = localStorage.getItem('shopID');
    if (query && queryType) {
      this.setState({
        query,
        queryType,
      });
      this.handleSubmit(undefined, query, queryType);
    } else if (shopID) {
      this.setState({
        query: shopID,
        queryType: 'SHOP ID',
      });
      localStorage.removeItem('shopID');
      this.handleSubmit(undefined, shopID, 'SHOP ID');
    }
  }

  async componentDidMount() {
    this.checkURLChange();
    let config = await getSystemConfig();
    this.setState({
      config: config,
    });

    this.setState({ windowWidth: window.outerWidth });
    window.addEventListener('resize', this.setWindowWidth);
    if (Array.isArray(this.state.queriedShops)) {
      let pageData: { [k: string]: any } = {};
      this.state.queriedShops.forEach((shop, index) => {
        pageData[index] = shop;
      });
      storeClipboard(pageData);
    }
  }

  componentWillUnmount() {
    // Setting state (global file variable) to this.state allowed persistance
    // of state when navigating back to the component later
    state = {
      ...this.state,
      isLoading: false,
    };
  }

  setWindowWidth = () => {
    setTimeout(this.setStateWidth.bind(this), 20);
  };

  setStateWidth() {
    this.setState({ windowWidth: window.outerWidth });
    if (this.state.windowWidth <= 500 && document.querySelectorAll('.rccs')) {
      (
        document.querySelectorAll('.rccs') as NodeListOf<HTMLDivElement>
      ).forEach((element) => {
        element.style.transform = `scale(${this.state.windowWidth / 500})`;
      });
    }
  }

  handleChange = async (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | string, // If event is from a FieldWithHistory the event will just be a string
    fieldId?: string
  ) => {
    const { queryType } = this.state;
    if (typeof event === 'string') {
      if (
        queryType === 'CREDIT CARD LAST 4' ||
        queryType === 'TRANSACTION LAST 4'
      ) {
        const reg = new RegExp(/^(\s{0}|\d+)$/);
        if (!(event.length < 5 && reg.test(event))) {
          return;
        }
      }
      let error = '';
      switch (this.state.queryType) {
        case 'SHOP ID':
        case 'OWNER ID':
          error = !uuidValidate(event.trim())
            ? 'ID must be in this format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
            : '';
          event = event.toLowerCase();
          break;
        case 'OWNER USERNAME':
          error = !usernameValidate(event.trim())
            ? 'Username must be at least 8 alphanumeric lowercase characters starting with a letter'
            : '';
          event = event.toLowerCase();
          break;
        case 'CUSTOMER ID':
          error = !customerIdValidate(event.trim())
            ? `Customer ID must be integer characters`
            : '';
          break;
      }
      this.setState({
        [fieldId as any]: event,
        queryError: error,
      } as any);
    } else if (event) {
      const { id, value } = event.target;

      this.setState({
        [id]: value,
      } as any);
    }
  };

  handleQueryTypeSelectionChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    let helpText = '' as string | JSX.Element;
    switch (e.target.value) {
      case 'SHOP NAME':
        helpText = (
          <ul>
            <li>
              {'SHOP NAME is a case-insensitive "contains" query, and can be ' +
                'partially matched (i.e., "T", "Te", "Tes" or "Test" will match "Test Auto Shop").'}
            </li>
            <li>
              {'You can also use NOT which applies to the whole string following ' +
                '(i.e. "NOT auto center" will match "Test Auto" but not "Test Auto Center").'}
            </li>
            <li>
              {'You can also use "&&" and "||" to make combination queries ' +
                '(i.e. "test && center" will match "Test Auto Center" but not "Test Shop".' +
                ' "test || center" will match "Testing Shop" and "Auto Center").'}
            </li>
            <li>
              {'You can also use "NOT" and "&&" and "||" in combination ' +
                '(i.e. "NOT test && center" will match "First Auto Center" but not "Test Auto Center".' +
                ' "NOT test || center" will match "First Auto Center" and "First Auto" but not "Test Auto").'}
            </li>
          </ul>
        );
        // Load shop names from localstorage or backend
        this.setState({
          loadingShopNames: true,
        } as any);
        let self = this;
        const flag = false;
        getLocalStorageShopNames(flag, (err: Error, finalResult: any) => {
          self.setState({
            shopNames: finalResult,
            loadingShopNames: false,
          });
        });

        break;
      case 'PHONE NUMBER':
        helpText = (
          <section>
            <p>
              PHONE NUMBER is a "contains" query that ignores non-digit
              characters, and can be partially matched.
            </p>
            <p>
              For example, the following queries will all match to the number:{' '}
              <strong>+11234567890</strong>
            </p>
            <pre>(123) 456-7890</pre>
            <pre>123-456-7890</pre>
            <pre>123.456.7890</pre>
            <pre>34567</pre>
            <pre>3.456.7</pre>
            <p>
              Note that "34567" and "3.456.7" could match to multiple numbers,
              such as <strong>+11234567890</strong>,{' '}
              <strong>+18934567123</strong>, etc.
            </p>
          </section>
        );
        break;
      case 'ADDRESS':
        helpText =
          'ADDRESS fields are case-sensitive "contains" queries, and can be partially matched. No field is required upon submission except Country.';
        break;
    }
    this.setState({
      queryType: e.target.value,
      query: '',
      queryError: '',
      helpText: helpText,
    } as any);
  };

  handleAddressChange = (addressObj: AddressQuery) => {
    this.setState({
      address: addressObj,
    });
  };

  handleChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filter: e.target.value,
    });

    const updateFilter = _.debounce((f: string) => {
      this.updateFilteredList(f);
    }, 300);

    updateFilter(e.target.value);
  };

  updateFilteredList = (filter: string) => {
    let shopList = this.state.queriedShops;

    if (filter.trim().length > 0) {
      const regexStr = generateFilterRegex(filter);
      shopList = this.state.queriedShops.filter((u) => {
        if (regexStr.test(u.shopName) || regexStr.test(u.shopID)) {
          return true;
        }
        return false;
      });
    }
    this.setState({
      queriedShopsFiltered: shopList || [],
    });
  };

  validateForm() {
    const { query, queryType } = this.state;

    switch (queryType) {
      case 'SHOP NAME':
        return this.state.shopNames && query.trim().length > 0;
      case 'SHOP ID':
      case 'OWNER ID':
        return uuidValidate(query.trim());
      case 'CUSTOMER ID':
        return customerIdValidate(query.trim());
      case 'OWNER USERNAME':
        return usernameValidate(query.trim());
      case 'ADDRESS':
        return Object.values(this.state.address as Object).some(
          (field) => field !== ''
        );
      case 'CREDIT CARD LAST 4':
        return query.trim().length === 4;
      case 'TRANSACTION LAST 4':
        return query.trim().length === 4;
      default:
        return query.trim().length > 0;
    }
  }

  handleSubmit = async (
    e?: React.SyntheticEvent,
    query?: string,
    queryType?: string
  ) => {
    e?.preventDefault();

    this.setState({ isLoading: true });
    if (!queryType) {
      queryType = this.state.queryType;
    }
    if (!query) {
      query = this.state.query;
    }

    try {
      let result: any = null;
      let user: any = null;

      switch (queryType) {
        case 'SHOP ID':
          const responses = await Promise.all([
            getShopSummary(query),
            getShopOrders(query),
            getShopEvents(query),
            getShopCustomers(query),
            getShopVehicles(query),
            getShopServices(query),
            getShopParts(query),
          ]);

          result = makeShopSummaryObject(responses);
          break;
        case 'SHOP NAME':
          const shopIDs = this.state.shopNames
            .filter((shop) => checkFilter(query, shop.shopName))
            .map((shop) => shop.shopID);
          if (shopIDs.length < 100) {
            result = await getShops(shopIDs);
          } else {
            result =
              'Too many results (' +
              shopIDs.length +
              ').  Please be more specific.';
          }
          // If there's only one result, just get the full summary and logs
          if (result.length === 1) {
            const responses = await Promise.all([
              getShopSummary(result[0].shopID),
              getShopOrders(result[0].shopID),
              getShopEvents(result[0].shopID),
              getShopCustomers(result[0].shopID),
              getShopVehicles(result[0].shopID),
              getShopServices(result[0].shopID),
              getShopParts(result[0].shopID),
            ]);
            result = makeShopSummaryObject(responses);
            result = [result];
          }
          break;
        case 'CREDIT CARD LAST 4':
        case 'TRANSACTION LAST 4':
        case 'PHONE NUMBER':
        case 'CUSTOMER ID':
          if (queryType === 'PHONE NUMBER' && query) {
            query = formatPhoneNumber(query);
          }
          result = await getShopSummaryByProperty(queryType, query);

          // If there's only one result, just get the full summary and logs
          if (result?.length === 1) {
            const responses = await Promise.all([
              getShopSummary(result[0].shopID),
              getShopOrders(result[0].shopID),
              getShopEvents(result[0].shopID),
              getShopCustomers(result[0].shopID),
              getShopVehicles(result[0].shopID),
              getShopServices(result[0].shopID),
              getShopParts(result[0].shopID),
            ]);
            result = makeShopSummaryObject(responses);
            result = [result];
          }
          break;
        case 'OWNER ID':
        case 'OWNER USERNAME':
          if (queryType === 'OWNER USERNAME') query = query?.toLowerCase();
          let apiQueryType = queryType === 'OWNER ID' ? 'USERID' : 'USERNAME';
          user = await getUserSummary(apiQueryType, query);

          if (user?.error === 'User record not found!') {
            this.props.handleShowAlert(
              'Error',
              'This user was not found, or does not exist!'
            );
            return;
          }

          let shop = user.userShops.find(
            (shop: Shop) => shop.shopUserRole === 'OWNER'
          );

          if (shop) {
            const responses = await Promise.all([
              getShopSummary(shop.shopID),
              getShopOrders(shop.shopID),
              getShopEvents(shop.shopID),
              getShopCustomers(shop.shopID),
              getShopVehicles(shop.shopID),
              getShopServices(shop.shopID),
              getShopParts(shop.shopID),
            ]);

            result = makeShopSummaryObject(responses);
          } else {
            this.props.handleShowAlert(
              'Error',
              'This user is not the OWNER of any shops.'
            );
            return;
          }
          break;

        case 'ADDRESS':
          result = await getShopSummaryByProperty(
            'ADDRESS',
            this.state.address
          );

          break;
        default:
          this.props.handleShowAlert(
            'Error',
            `This query type is not valid: ${this.state.queryType}.`,
            false
          );
          break;
      }

      if (!result || Object.hasOwn(result, 'error')) {
        if ([401, 403].indexOf(result.error.status) === -1) {
          this.props.handleShowAlert(
            'Error',
            <span>
              {`Could not find a shop using a ${queryType} for this query: `}
              <strong>{query}</strong>
            </span>,
            false
          );
        }
        this.setState({
          showShopInfo: false,
          queriedShops: [],
          queriedShopsFiltered: [],
        });
        return;
      } else if (typeof result === 'string') {
        this.props.handleShowAlert(
          'Error',
          <span>
            {result}
            <strong>{query}</strong>
          </span>,
          false
        );

        this.setState({
          showShopInfo: false,
          queriedShops: [],
          queriedShopsFiltered: [],
        });
        return;
      } else if (result.length === 0) {
        this.props.handleShowAlert(
          'Error',
          <span>
            {`Could not find a shop using a ${queryType} for this query: `}
            <strong>{query}</strong>
          </span>,
          false
        );
        this.setState({
          showShopInfo: false,
          queriedShops: [],
          queriedShopsFiltered: [],
        });
      } else {
        if (
          queryType === 'SHOP ID' ||
          queryType === 'OWNER ID' ||
          queryType === 'OWNER USERNAME'
        ) {
          result = [result];
        }

        if (Array.isArray(result)) {
          let pageData: { [k: string]: any } = {};
          result.forEach((shop, index) => {
            pageData[index] = shop;
          });
          storeClipboard(pageData);
        }

        this.setState({
          queriedShops: result,
          queriedShopsFiltered: result,
          showShopInfo: true,
        });
      }
    } catch (e: any) {
      if (!e || Object.hasOwn(e, 'error')) {
        if ([401, 403].indexOf(e.error.status) === -1) {
          this.props.handleShowAlert(
            'Error',
            <span>
              {`Could not find a shop using a ${queryType} for this query: `}
              <strong>{query}</strong>
            </span>,
            false
          );
        } else {
          return;
        }
      }
      console.log(e.message);
      this.setState({
        showShopInfo: false,
        queriedShops: [],
        queriedShopsFiltered: [],
      });
    } finally {
      this.setState({
        isLoading: false,
      });

      let recentSearches = getRecentSearches(queryType);

      if (recentSearches) {
        if (recentSearches.length > 4) recentSearches.pop();
        if (query && recentSearches.indexOf(query) === -1)
          recentSearches.unshift(query);
      } else {
        recentSearches = query ? [query] : [];
      }
      setRecentSearches(queryType, recentSearches);
    }
  };

  getPlaceholder = () => {
    switch (this.state.queryType) {
      case 'SHOP ID':
      case 'OWNER ID':
        return "ex. 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'";
      case 'CUSTOMER ID':
        return this.state.config.braintreeMode === 'sandbox'
          ? "ex. 'xxxxxxxxx'"
          : "ex. 'xxxxxxxxxx'";
      case 'SHOP NAME':
        return "ex. 'John's Auto'";
      case 'OWNER USERNAME':
        return "ex. 'john.smith'";
      case 'CREDIT CARD LAST 4':
      case 'TRANSACTION LAST 4':
        return 'ex. 1234';
      case 'PHONE NUMBER':
        return 'ex. +###########, +# ### ### ####, +#(###)###-####';
    }
  };

  getQueryTypeHelper = () => {
    switch (this.state.queryType) {
      case 'SHOP ID':
      case 'OWNER ID':
      case 'OWNER USERNAME':
        return `${this.state.queryType} (lowercase)`;
      case 'ADDRESS':
        return `${this.state.queryType} (case-sensitive)`;
      case 'SHOP NAME':
      case 'PHONE NUMBER':
        return this.state.queryType;
    }
  };


  render() {
    const {
      queriedShops,
      queriedShopsFiltered,
      query,
      queryType,
      queryError,
      helpText,
      currentShopId,
      isLoading,
      filter,
      showShopInfo,
    } = this.state;

    const isNotExternal = this.props.user.userType !== 'EXTERNAL';
    const isSingleShopResult = queriedShops.length === 1;

    let shopInfoAccordions = queriedShopsFiltered
      .sort((a, b) => a.shopName.localeCompare(b.shopName))
      .map((shop: Shop) => (
        <ShopInfoAccordion
          key={shop.shopID}
          shop={shop}
          getShop={(query?: string, queryType?: string) =>
            this.handleSubmit(undefined, query, queryType)
          }
          queryType={queryType}
          setQueryType={(queryType: string) =>
            this.setState({ queryType: queryType })
          }
          setQuery={(query: string) => this.setState({ query: query })}
          isSingleShopResult={isSingleShopResult}
          currentShopId={currentShopId}
          setCurrentShopId={(shopID: string) =>
            this.setState({ currentShopId: shopID })
          }
          isLoading={isLoading}
          setIsLoading={(isLoading: boolean) =>
            this.setState({ isLoading: isLoading })
          }
        />
      ));

    return (
      <>
        <Header context="Shop Functions" title="Get Shop Summary" />
        <FloatingButton
          onClick={() => {
            if (topRef && topRef.current) {
              topRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }}
        />
        <div className="l-container" ref={topRef}>
          <form>
            <div className="c-field">
              <label htmlFor="queryType" className="c-field__label">
                Query Type
              </label>
              <div className="c-select">
                <select
                  onChange={this.handleQueryTypeSelectionChange}
                  id="queryType"
                  value={queryType}
                >
                  {(isNotExternal ? [
                    'SHOP ID',
                    'SHOP NAME',
                    'OWNER ID',
                    'OWNER USERNAME',
                    'CUSTOMER ID',
                    'CREDIT CARD LAST 4',
                    'TRANSACTION LAST 4',
                    'ADDRESS',
                    'PHONE NUMBER',
                  ] : [
                    'SHOP ID',
                    'SHOP NAME',
                    'OWNER USERNAME',
                    'CREDIT CARD LAST 4',
                    'TRANSACTION LAST 4',
                    'ADDRESS',
                    'PHONE NUMBER',
                  ]).map((type, i) => {
                    return <option key={i}>{type}</option>;
                  })}
                </select>
              </div>
            </div>

            {queryType !== 'ADDRESS' ? (
              <>
                <div
                  className={`c-field ${queryError && 'u-margin-bottom-none'}`}
                >
                  <label
                    htmlFor="query"
                    className="l-flex-align-center c-field__label u-margin-top-large"
                  >
                    {this.getQueryTypeHelper()}
                    {helpText && (
                      <small>
                        <HelpTooltip
                          label={helpText}
                          additionalStyles={{ fontSize: '12px' }}
                          onClick={() =>
                            this.props.handleShowAlert &&
                            this.props.handleShowAlert('Info', helpText, false)
                          }
                        />
                      </small>
                    )}
                  </label>

                  <FieldWithHistory
                    fieldId="query"
                    searchKey={queryType}
                    selected={query}
                    onInputChange={this.handleChange}
                    onChange={this.handleChange}
                    placeholder={this.getPlaceholder()}
                    onEnter={() => {
                      if (this.validateForm()) this.handleSubmit();
                    }}
                  />
                </div>
                {queryError && (
                  <div className="u-margin-bottom-large u-text-error u-margin-left-large">
                    <small>{queryError}</small>
                  </div>
                )}
              </>
            ) : (
              <>
                {helpText && (
                  <small>
                    <HelpTooltip
                      label={helpText}
                      additionalStyles={{
                        fontSize: '12px',
                        width: 'fit-content',
                      }}
                      onClick={() =>
                        this.props.handleShowAlert &&
                        this.props.handleShowAlert('Info', helpText, false)
                      }
                    />
                  </small>
                )}
                <AddressForm handleChange={this.handleAddressChange} />
              </>
            )}
            <div className="c-field">
              <LoaderButton
                disabled={!this.validateForm() || this.state.loadingShopNames}
                isLoading={isLoading}
                text={
                  this.state.loadingShopNames ? 'Please Wait...' : 'Get Summary'
                }
                loadingText="Searching..."
                onClick={this.handleSubmit}
              />
            </div>
            {queryType === 'SHOP NAME' && (
              <div className="c-field">
                <LoaderButton
                  type="button"
                  disabled={false}
                  isLoading={this.state.loadingShopNames}
                  text={this.state.loadingShopNames ? 'Syncing...' : 'Sync'}
                  loadingText="Syncing..."
                  onClick={() => {
                    window.indexedDB.deleteDatabase('ShopNames');
                    this.setState({
                      loadingShopNames: true,
                      shopNames: [],
                    } as any);
                    let self = this;
                    const flag = true;
                    getLocalStorageShopNames(
                      flag,
                      (err: Error, finalResult: any) => {
                        if (err) {
                          console.log('Error: ', err);
                        }
                        self.setState({
                          shopNames: finalResult,
                          loadingShopNames: false,
                        });
                      }
                    );
                  }}
                />
              </div>
            )}
          </form>
        </div>
        <FadeIn visible={!isLoading && showShopInfo}>
          {queriedShops.length > 2 && (
            <>
              <div>
                <h2>{queriedShopsFiltered.length} result(s) found:</h2>
              </div>
              <div className="c-field u-margin-top-small u-margin-right-small">
                <label
                  htmlFor="filter"
                  className="c-field__label u-is-vishidden"
                >
                  Filter
                </label>
                <input
                  type="text"
                  id="filter"
                  maxLength={50}
                  className="c-input u-font-mono"
                  placeholder="Filter (Shop Name, Shop ID)"
                  value={filter}
                  onChange={this.handleChangeFilter}
                />
                <i className="c-field__input-icon fal fa-search" />
              </div>
            </>
          )}
          <div>{shopInfoAccordions}</div>
        </FadeIn>
      </>
    );
  }
}
